<div class="tile-padding-first tile-padding admin-page">

  <div class="tw:my-4 tw:p-3 tw:border tw:border-sky-500 tw:bg-sky-50 tw:text-sky-900 tw:rounded" data-test="banner">
    New features are off unless turned on here. Changes apply to everyone on their next page load, within about 30 seconds.
    Use Edit to narrow a feature to roles, brands, named users, a rollout percentage or dates; turning it off always wins.
    Runtime settings, such as when an action counts as overdue, are on the Settings tab of System Controls.
  </div>

  <!-- How the stage buttons work, while switch stages are in use (featureswitches.lifecycle). -->
  @if (lifecycleEnabled) {
    <details class="tw:my-4 tw:p-3 tw:border tw:border-gray-300 tw:rounded" data-test="stages-help">
      <summary class="tw:cursor-pointer tw:font-medium">How stages work: Dark, Rolling out and Adopted</summary>
      <div class="tw:mt-2 tw:text-sm">
        <p>Each feature has a stage as well as its on/off switch. Turning a feature off always hides it from everyone, whatever its stage.</p>
        <dl class="tw:mt-2">
          <dt class="tw:font-medium">Dark</dt>
          <dd class="tw:ml-4 tw:mb-2">Nobody sees it, even while it is switched on. Every new feature starts here.
            <strong>Set dark</strong> hides a feature straight away without losing its targeting, so it can be brought back exactly as it was.</dd>
          <dt class="tw:font-medium">Rolling out</dt>
          <dd class="tw:ml-4 tw:mb-2">While it is on, its targeting decides who sees it: the rollout percentage, roles, brands, named users and dates set with Edit.
            If it gates a page (the row says "Gates: …"), people must also hold that feature on Role Features.
            <strong>Start rolling out</strong> and <strong>Back to rolling out</strong> move a feature here.</dd>
          <dt class="tw:font-medium">Adopted</dt>
          <dd class="tw:ml-4 tw:mb-2">The feature is part of the product. While it is on, everyone who holds its feature on Role Features sees it (everyone, if it gates nothing), and its targeting is ignored.
            From then on, who can use it is managed on Role Features, not here. <strong>Adopt</strong> moves a feature here.</dd>
        </dl>
        <p>Changing a stage is admin-only, asks first, and is recorded in the feature's History. Hover over a stage button for what it will do to that feature.</p>
      </div>
    </details>
  }
  @if (error) {
    <div class="tw:my-4 tw:p-3 tw:border tw:border-red-500 tw:bg-red-50 tw:text-red-900 tw:rounded" data-test="error">
      {{error}}
    </div>
  }

  <!-- Bulk turn-off, behind featureswitches.bulk-off. Every action lists what will go off and asks first.
       There is deliberately no bulk turn-on. -->
  @if (bulkOffEnabled && !loading) {
    <section class="admin-panel" data-test="bulk-off-panel">
      <h2 class="admin-h2">Turn features off</h2>
      <p class="admin-lede">
        Turn several features off at once. You see exactly which ones first, and nothing here turns a feature on.
        This panel's own switch is left on; turn it off on its row.
      </p>
      <div class="admin-toolbar">
        <button type="button" class="btn" [disabled]="bulkBusy" (click)="turnAllOff()" data-test="bulk-off-all">Turn all off</button>
        <div>
          <label class="field__label" for="bulkOffSince">Turn off features introduced on or after</label>
          <input id="bulkOffSince" type="date" class="field__control" [value]="bulkSince"
            (change)="bulkSince = $any($event.target).value" data-test="bulk-off-date" />
        </div>
        <label class="tw:flex tw:items-center tw:gap-2 tw:h-[38px]">
          <input type="checkbox" [checked]="bulkThisTabOnly" (change)="bulkThisTabOnly = $any($event.target).checked"
            data-test="bulk-off-this-tab" />
          Only the {{currentTabLabel()}} tab
        </label>
        @if (lifecycleEnabled) {
          <label class="tw:flex tw:items-center tw:gap-2 tw:h-[38px]">
            <input type="checkbox" [checked]="bulkIncludeAdopted" (change)="bulkIncludeAdopted = $any($event.target).checked"
              data-test="bulk-off-include-adopted" />
            Include adopted switches
          </label>
        }
        <button type="button" class="btn" [disabled]="bulkBusy || !bulkSince" (click)="turnOffSince()" data-test="bulk-off-since">Turn off</button>
      </div>
      @if (bulkStatus) {
        <p class="tw:mt-3 tw:mb-0" role="status" data-test="bulk-off-status">{{bulkStatus}}</p>
      }
    </section>
  }

  @if (loading) {
    <p>Loading…</p>
  }

  @if (!loading) {
    <mat-tab-group [selectedIndex]="selectedIndex" (selectedIndexChange)="onTabChange($event)" animationDuration="0ms" data-test="category-tabs">
      @for (tab of tabs; track tab.key) {
        <mat-tab [label]="tab.label">
          <!-- matTabContent: a tab's body (and its panels' API calls) is only created when the tab is opened. -->
          <ng-template matTabContent>
          <div class="tw:pt-4" [attr.data-test]="'tab-' + tab.key">

            @if (tab.switches.length > 0) {
              <div class="tw:overflow-x-auto">
                <table class="admin-table">
                  <thead>
                    <tr>
                      <th class="tw:text-left">Feature</th>
                      <th class="tw:text-left tw:w-64">Last changed</th>
                      <th class="tw:text-left tw:w-24">On</th>
                      <th class="tw:text-left tw:w-40"></th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (row of tab.rows; track row.item.switchKey) {
                      @let item = row.item;
                      <tr [class.tw:opacity-60]="!item.isEnabled || !!row.blockedBy" data-test="switch-row"
                        [attr.data-depth]="row.depth" [attr.data-sub-switch]="row.depth > 0 ? '' : null">
                        <!-- A sub-switch is indented under its parent; the border ties it to the row above. -->
                        <td [style.padding-left.rem]="row.depth > 0 ? 0.75 + row.depth * 1.5 : null"
                          [class.tw:border-l-4]="row.depth > 0" [class.tw:border-gray-300]="row.depth > 0">
                          <strong>{{item.displayName}}</strong>
                          @if (lifecycleEnabled && stageLabel(item)) {
                            <span class="tw:ml-2 tw:text-xs tw:px-2 tw:py-0.5 tw:rounded" data-test="switch-stage" [attr.data-stage]="item.stage"
                              [class.tw:bg-gray-200]="item.stage === 'Dark'" [class.tw:text-gray-800]="item.stage === 'Dark'"
                              [class.tw:bg-amber-100]="item.stage === 'RollingOut'" [class.tw:text-amber-900]="item.stage === 'RollingOut'"
                              [class.tw:bg-green-100]="item.stage === 'Adopted'" [class.tw:text-green-900]="item.stage === 'Adopted'">{{stageLabel(item)}}</span>
                          }
                          @if (row.blockedBy) {
                            <!-- Its own toggle still saves; it just has no effect until the parent is on. -->
                            <span class="tw:ml-2 tw:text-xs tw:px-2 tw:py-0.5 tw:rounded tw:bg-amber-100 tw:text-amber-900"
                              data-test="switch-needs" title="Takes effect only while {{row.blockedBy}} is on">needs {{row.blockedBy}}</span>
                          }
                          @if (item.description) {
                            <div class="tw:text-sm tw:opacity-75">{{item.description}}</div>
                          }
                          @if (lifecycleEnabled) {
                            @if (item.featureName) {
                              <div class="tw:text-sm" data-test="switch-gates">Gates: {{item.featureName}}</div>
                            }
                            @if (item.stage === 'Dark') {
                              <div class="tw:text-sm tw:opacity-75" data-test="switch-dark-note">Nobody sees this until it is rolling out.</div>
                            }
                          }
                          <!-- Targeting only; greyed out while the switch is off, because it does not apply then. -->
                          <div class="tw:text-sm" [class.tw:opacity-50]="!item.isEnabled" [class.switch-summary--muted]="!item.isEnabled"
                            data-test="switch-summary">{{summary(item)}}</div>
                          <div class="tw:text-xs tw:opacity-50">
                            {{item.switchKey}}
                            @if (item.introducedOn) {
                              <span data-test="switch-introduced"> · Introduced {{item.introducedOn | date:'dd/MM/yyyy'}}</span>
                            }
                          </div>
                        </td>
                        <td data-label="Last changed">
                          @if (item.updatedUtc) {
                            <admin-email [value]="item.updatedBy || 'unknown'" /><br />
                            <span class="tw:text-sm tw:opacity-75">{{item.updatedUtc | date:'dd/MM/yyyy HH:mm'}}</span>
                          } @else {
                            <span class="tw:text-sm tw:opacity-50">Never changed</span>
                          }
                        </td>
                        <td data-label="On">
                          <label class="field__toggle">
                            <!-- preventDefault leaves the switch as it was: it only moves once the server returns the updated row. -->
                            <input type="checkbox" role="switch" [checked]="item.isEnabled"
                              [attr.aria-checked]="item.isEnabled" [attr.aria-label]="item.displayName"
                              [disabled]="saving === item.switchKey"
                              (click)="$event.preventDefault(); toggle(item)" data-test="switch-toggle" />
                            <span class="field__toggle-track"></span>
                          </label>
                        </td>
                        <td>
                          <button type="button" class="btn" [disabled]="saving === item.switchKey" (click)="edit(item)" data-test="edit">Edit</button>
                          <button type="button" class="btn" (click)="history(item)" data-test="history">History</button>
                          @if (lifecycleEnabled && isAdmin) {
                            <div class="tw:mt-1" data-test="stage-actions">
                              @for (action of stageActions(item); track action.to) {
                                <button type="button" class="btn" [disabled]="staging === item.switchKey"
                                  [title]="action.hint" [attr.aria-description]="action.hint"
                                  (click)="changeStage(item, action)" [attr.data-stage-to]="action.to" data-test="stage-action">{{action.label}}</button>
                              }
                            </div>
                          }
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            } @else if (!error) {
              <p class="tw:opacity-60" data-test="tab-empty">No new features in this category.</p>
            }

            @if (tab.key === 'sync') {
              <ng-container *ngComponentOutlet="staffSyncPanel"></ng-container>
              <ng-container *ngComponentOutlet="roleMapEditor"></ng-container>
            }
          </div>
          </ng-template>
        </mat-tab>
      }
    </mat-tab-group>
  }

</div>
